import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import ImagePicker from './ImagePicker';
import HeaderEditor from './editors/HeaderEditor';
import HeroEditor from './editors/HeroEditor';
import FAQEditor from './editors/AboutEditor';
import ServicesEditor from './editors/ServicesEditor';
import ContactEditor from './editors/ContactEditor';
import TestimonialsEditor from './editors/TestimonialsEditor';
import FooterEditor from './editors/FooterEditor';
import GalleryEditor from './editors/GalleryEditor';
import TeamEditor from './editors/TeamEditor';
import FeaturesEditor from './editors/FeaturesEditor';
import PricingEditor from './editors/PricingEditor';
import CTAEditor from './editors/CTAEditor';
import ArticleEditor from './editors/ArticleEditor';
import IframeEditor from './editors/IframeEditor';
import BannerEditor from './editors/BannerEditor';
import CustomCodeEditor from './editors/CustomCodeEditor';

const SectionEditor = ({ section, site, onUpdate, onClose, canEditHeaderFooter = true, isMainPage = true, onCreateCustomTheme }) => {
  const [localSection, setLocalSection] = useState(section);
  const [uploading, setUploading] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerField, setImagePickerField] = useState('');
  const [imagePickerQuery, setImagePickerQuery] = useState('');
  const [imagePickerProvider, setImagePickerProvider] = useState('unsplash');

  useEffect(() => {
    console.log('SectionEditor: useEffect setting localSection with section:', section);
    console.log('SectionEditor: Incoming section logoType:', section.content?.logoType);
    setLocalSection(section);
  }, [section]);


  const handleContentChange = (field, value) => {
    console.log('SectionEditor: handleContentChange called with field:', field, 'value:', value);
    const updatedSection = {
      ...localSection,
      content: {
        ...(localSection.content || {}),
        [field]: value
      }
    };
    console.log('SectionEditor: Updated section content:', updatedSection.content);
    console.log('SectionEditor: Specific logoType in updatedSection:', updatedSection.content.logoType);
    setLocalSection(updatedSection);
    
    // Immediately propagate changes to parent
    onUpdate(updatedSection);
  };

  // Handle multiple field updates in one operation
  const handleMultipleContentChanges = (updates) => {
    const updatedSection = {
      ...localSection,
      content: {
        ...(localSection.content || {}),
        ...updates
      }
    };
    setLocalSection(updatedSection);
    
    // Update preview immediately with the updated section
    onUpdate(updatedSection);
  };

  const handleImageSelect = (imageData) => {
    let updatedSection = { ...localSection };
    
    // Handle nested field paths (e.g., "services.0.imageUrl")
    if (imagePickerField.includes('.')) {
      const fieldParts = imagePickerField.split('.');
      const [arrayName, indexStr, fieldName] = fieldParts;
      const index = parseInt(indexStr);
      
      if (arrayName && !isNaN(index) && fieldName) {
        const array = [...(localSection.content?.[arrayName] || [])];
        if (array[index]) {
          array[index] = {
            ...array[index],
            [fieldName]: imageData.url,
            [`${fieldName}Credit`]: imageData.credit
          };
          updatedSection = {
            ...localSection,
            content: {
              ...(localSection.content || {}),
              [arrayName]: array
            }
          };
        }
      }
    } else {
      // Handle simple field paths
      if (imagePickerField === 'logoImage') {
        // Special handling for logo images - set both logoImage and logoImageUrl
        updatedSection = {
          ...localSection,
          content: {
            ...(localSection.content || {}),
            logoImage: imageData.url,
            logoImageUrl: imageData.url,
            logoImageCredit: imageData.credit
          }
        };
      } else {
        // Handle other simple fields normally
        updatedSection = {
          ...localSection,
          content: {
            ...(localSection.content || {}),
            [imagePickerField]: imageData.url,
            [`${imagePickerField}Credit`]: imageData.credit
          }
        };
      }
    }
    
    setLocalSection(updatedSection);
    setTimeout(() => onUpdate(updatedSection), 0);
    setImagePickerOpen(false);
    setImagePickerField('');
    setImagePickerQuery('');
    setImagePickerProvider('unsplash');
  };

  // FOCUSED: Generate simple search terms (max 2-3 keywords)
  const generateSearchQuery = (field, customQuery = '') => {
    if (customQuery) return customQuery;
    
    // NEW: Use stored AI-generated image search keywords
    const imageSearchKeywords = localSection.imageSearchKeywords;
    if (imageSearchKeywords && imageSearchKeywords.length > 0) {
      // Use the first keyword as the primary search term
      const primaryKeyword = imageSearchKeywords[0];
      console.log(`🎨 Using AI-generated keyword: "${primaryKeyword}" for ${localSection.type} ${field}`);
      return primaryKeyword;
    }
    
    // FALLBACK: Legacy business type detection for sections without stored keywords
    const businessInfo = site?.businessInfo;
    const content = localSection.content || {};
    
    let businessType = 'business';
    if (businessInfo?.description) {
      const desc = businessInfo.description.toLowerCase();
      const businessTypes = {
        'restaurant': ['restaurant', 'food', 'dining', 'cafe', 'bistro'],
        'fitness': ['fitness', 'gym', 'training', 'workout'],
        'tech': ['technology', 'software', 'tech', 'digital'],
        'beauty': ['beauty', 'salon', 'spa', 'skincare'],
        'consulting': ['consulting', 'business', 'strategy'],
        'design': ['design', 'creative', 'graphic'],
        'medical': ['medical', 'health', 'clinic', 'doctor'],
        'legal': ['legal', 'law', 'attorney'],
        'pets': ['pet', 'animal', 'veterinary', 'petshop', 'grooming'],
        'retail': ['retail', 'store', 'shop'],
        'education': ['education', 'school', 'teaching']
      };
      
      for (const [type, keywords] of Object.entries(businessTypes)) {
        if (keywords.some(keyword => desc.includes(keyword))) {
          businessType = type;
          break;
        }
      }
    }
    
    // Legacy fallback query generation
    let query = '';
    
    if (localSection.type === 'hero') {
      if (field === 'backgroundImageUrl') {
        query = `${businessType} business`;
      } else if (field === 'foregroundImageUrl') {
        query = `${businessType} professional`;
      }
    } else if (localSection.type === 'services') {
      if (field.includes('services.') && field.includes('.imageUrl')) {
        const match = field.match(/services\.(\d+)\.imageUrl/);
        if (match) {
          const index = parseInt(match[1]);
          const service = content.services?.[index];
          if (service?.title) {
            const serviceWords = service.title.toLowerCase()
              .split(/\s+/)
              .filter(word => word.length > 2)
              .slice(0, 2);
            query = serviceWords.join(' ') || `${businessType} service`;
          } else {
            query = `${businessType} service`;
          }
        }
      } else {
        query = `${businessType} work`;
      }
    } else if (localSection.type === 'gallery') {
      query = `${businessType} portfolio`;
    } else if (localSection.type === 'faq') {
      query = `${businessType} office`;
    } else if (localSection.type === 'contact') {
      query = `${businessType} location`;
    } else if (localSection.type === 'testimonials') {
      if (field.includes('.avatar')) {
        query = 'professional portrait';
      }
    } else if (localSection.type === 'team') {
      if (field.includes('.imageUrl')) {
        query = 'professional headshot';
      }
    } else if (localSection.type === 'header') {
      if (field === 'logoImage') {
        query = `${businessType} logo`;
      }
    }
    
    const finalQuery = query || `${businessType} professional`;
    console.log(`⚡ Legacy fallback query: "${finalQuery}" for ${localSection.type} ${field}`);
    
    return finalQuery;
  };

  // Validate query focus (for debugging)
  const validateQueryLength = (query) => {
    const wordCount = query.trim().split(/\s+/).length;
    return {
      query,
      wordCount,
      isFocused: wordCount <= 3,
      status: wordCount <= 2 ? '✅ Excellent' : wordCount === 3 ? '🟡 Good' : '❌ Too complex'
    };
  };

  const openImagePicker = (field, query = '', provider = 'unsplash') => {
    setImagePickerField(field);
    const searchQuery = generateSearchQuery(field, query);
    setImagePickerQuery(searchQuery);
    setImagePickerProvider(provider);
    setImagePickerOpen(true);
  };

  const handleVisibilityToggle = () => {
    const updatedSection = {
      ...localSection,
      visible: !localSection.visible
    };
    setLocalSection(updatedSection);
    
    // Update preview immediately using setTimeout to avoid setState during render
    setTimeout(() => onUpdate(updatedSection), 0);
  };

  const handleArrayFieldChange = (field, index, propertyOrValue, value) => {
    const array = localSection.content?.[field] || [];
    const newArray = [...array];
    
    if (value !== undefined) {
      // Update specific property of the object (for FAQ questions/answers)
      // Called as: handleArrayFieldChange('faqs', 0, 'question', 'New question')
      newArray[index] = {
        ...newArray[index],
        [propertyOrValue]: value
      };
    } else {
      // Replace entire object (for backward compatibility)
      // Called as: handleArrayFieldChange('field', 0, newObjectValue)
      newArray[index] = propertyOrValue;
    }
    
    handleContentChange(field, newArray);
  };

  const handleAddArrayItem = (field, value) => {
    const array = localSection.content?.[field] || [];
    const newArray = [...array, value];
    handleContentChange(field, newArray);
  };

  const handleRemoveArrayItem = (field, index) => {
    const array = localSection.content?.[field] || [];
    const newArray = array.filter((_, i) => i !== index);
    handleContentChange(field, newArray);
  };

  const handleImageUpload = async (file, field, customName = null) => {
    if (!file) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (customName) {
        formData.append('customName', customName);
      }

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Upload failed');
      }

      // Handle nested field paths (e.g., "services.0.imageUrl" or "testimonials.1.avatar")
      if (field.includes('.')) {
        const parts = field.split('.');
        const [arrayField, index, property] = parts;
        
        if (arrayField === 'images') {
          const images = [...(localSection.content[arrayField] || [])];
          if (!images[index]) images[index] = {};
          images[index][property] = result.url;
          handleContentChange(arrayField, images);
        } else if (arrayField === 'services') {
          const services = [...(localSection.content[arrayField] || [])];
          if (!services[index]) services[index] = {};
          services[index][property] = result.url;
          handleContentChange(arrayField, services);
        } else if (arrayField === 'testimonials') {
          const testimonials = [...(localSection.content[arrayField] || [])];
          if (!testimonials[index]) testimonials[index] = {};
          testimonials[index][property] = result.url;
          handleContentChange(arrayField, testimonials);
        }
      } else {
        // Handle simple fields like logoImage, backgroundImageUrl, etc.
        if (field === 'logoImage') {
          handleContentChange('logoImage', result.url);
          handleContentChange('logoImageUrl', result.url);
        } else {
          handleContentChange(field, result.url);
        }
      }
      
      // Clear the file input for subsequent uploads
      let inputId;
      if (field.includes('.')) {
        const parts = field.split('.');
        if (parts[0] === 'images') {
          inputId = `galleryImageInput-${parts[1]}`;
        } else if (parts[0] === 'services') {
          inputId = `serviceImageInput${parts[1]}`;
        } else if (parts[0] === 'testimonials') {
          inputId = `testimonialAvatarInput${parts[1]}`;
        }
      } else {
        if (field === 'foregroundImageUrl') {
          inputId = 'foregroundImageInput';
        } else if (field === 'backgroundImageUrl') {
          inputId = 'backgroundImageInput';
        } else if (field === 'logoImage') {
          inputId = 'logoImageInput';
          // Also clear the logo replacement input if it exists
          const replaceInput = document.getElementById('logoReplaceInput');
          if (replaceInput) replaceInput.value = '';
        }
      }
      
      if (inputId) {
        const input = document.getElementById(inputId);
        if (input) input.value = '';
      }
      
      toast.success('Image uploaded successfully!');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const renderFieldEditor = () => {
    const content = localSection.content || {};
    
    const businessInfo = site?.businessInfo;
    
    // Common props for all editors
    const commonProps = {
      content,
      onContentChange: handleContentChange,
      onMultipleContentChanges: handleMultipleContentChanges,
      onArrayFieldChange: handleArrayFieldChange,
      onAddArrayItem: handleAddArrayItem,
      onRemoveArrayItem: handleRemoveArrayItem,
      uploading,
      onImageUpload: handleImageUpload,
      onOpenImagePicker: openImagePicker,
      businessInfo,
      site, // Pass site data for custom themes
      onCreateCustomTheme // Pass theme creation handler
    };
    
    switch (localSection.type) {
      case 'header':
        if (!canEditHeaderFooter) {
          return (
            <div className="space-y-3">
              <div className="text-center py-4">
                <div className="text-sm font-medium text-amber-600 mb-2">
                  📝 Header Editing Restricted
                </div>
                <div className="text-xs text-gray-600 leading-relaxed">
                  Header content can only be edited from the main page.
                  <br />
                  You can show/hide the header using the visibility toggle above.
                </div>
              </div>
              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                <strong>Current Header:</strong> {content.companyName || content.businessName || 'No company name set'}
                {content.navigation && content.navigation.length > 0 && (
                  <div className="mt-1">
                    <strong>Navigation:</strong> {content.navigation.map(nav => 
                      typeof nav === 'string' ? nav : (nav.text || nav.label || 'Link')
                    ).join(' • ')}
                  </div>
                )}
              </div>
            </div>
          );
        }
        return <HeaderEditor {...commonProps} />;
        
      case 'hero':
        return <HeroEditor {...commonProps} />;
        
      case 'faq':
        return <FAQEditor {...commonProps} />;
        
      case 'services':
        return <ServicesEditor {...commonProps} />;
        
      case 'contact':
        return <ContactEditor {...commonProps} />;
        
      case 'testimonials':
        return <TestimonialsEditor {...commonProps} />;
        
      case 'gallery':
        return <GalleryEditor {...commonProps} />;
        
      case 'team':
        return <TeamEditor {...commonProps} />;
        
      case 'features':
        return <FeaturesEditor {...commonProps} />;
        
      case 'pricing':
        return <PricingEditor {...commonProps} />;
        
      case 'cta':
        return <CTAEditor {...commonProps} />;
        
      case 'article':
        return <ArticleEditor {...commonProps} />;
        
      case 'iframe':
        return <IframeEditor {...commonProps} />;
        
      case 'banner':
        return <BannerEditor {...commonProps} />;
        
      case 'custom-code':
        return <CustomCodeEditor {...commonProps} />;
        
      case 'footer':
        if (!canEditHeaderFooter) {
          return (
            <div className="space-y-3">
              <div className="text-center py-4">
                <div className="text-sm font-medium text-amber-600 mb-2">
                  📝 Footer Editing Restricted
                </div>
                <div className="text-xs text-gray-600 leading-relaxed">
                  Footer content can only be edited from the main page.
                  <br />
                  You can show/hide the footer using the visibility toggle above.
                </div>
              </div>
              <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                <strong>Current Footer:</strong>
                {content.email && <div>Email: {content.email}</div>}
                {content.phone && <div>Phone: {content.phone}</div>}
                {content.address && <div>Address: {content.address}</div>}
                {content.socialLinks && content.socialLinks.length > 0 && (
                  <div>Social: {content.socialLinks.map(link => link.platform).filter(Boolean).join(', ')}</div>
                )}
                {!content.email && !content.phone && !content.address && (!content.socialLinks || content.socialLinks.length === 0) && (
                  <div>Footer configured on main page</div>
                )}
              </div>
            </div>
          );
        }
        return <FooterEditor {...commonProps} />;

      default:
        return (
          <div className="text-sm text-gray-500">
            Editor for {localSection.type} section coming soon...
          </div>
        );
    }
  };

  return (
    <div className="p-1">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
          {localSection.type} Section
        </h4>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="space-y-1">
        {/* Visibility Toggle */}
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded ">
          <span className="text-xs font-medium text-gray-700">Visibility</span>
          <button
            onClick={handleVisibilityToggle}
            className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-all ${
              localSection.visible
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            {localSection.visible ? (
              <>
                <Eye className="w-3 h-3 mr-1" />
                On
              </>
            ) : (
              <>
                <EyeOff className="w-3 h-3 mr-1" />
                Off
              </>
            )}
          </button>
        </div>

        {/* Content Editor */}
        <div className="bg-gray-50  p-1">
          {renderFieldEditor()}
        </div>
      </div>
      
      {/* Image Picker Modal */}
      <ImagePicker
        isOpen={imagePickerOpen}
        onClose={() => {
          setImagePickerOpen(false);
          setImagePickerField('');
          setImagePickerQuery('');
          setImagePickerProvider('unsplash');
        }}
        onSelect={handleImageSelect}
        searchQuery={imagePickerQuery}
        orientation="landscape"
        provider={imagePickerProvider}
      />
    </div>
  );
};

export default SectionEditor;